/**
 * Generic provider config page: shared layout used by Bolt, Glovo, Uber, InDriver.
 * Each provider page passes its specific metadata and the page renders identically.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Copy,
  Check,
  AlertCircle,
  Zap,
  Webhook,
  Clock,
  Info,
} from 'lucide-react';
import { deliveryService } from '@services/delivery.service';
import type { DeliveryProviderSlug, WebhookEvent, ApiCallLog } from '@services/delivery.service';
import { PageGuard } from '@components/PageGuard';
import { cn } from '@lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProviderMeta {
  slug: DeliveryProviderSlug;
  name: string;
  tagline: string;
  colorClass: string;       // bg-[...] class for the icon background
  initial: string;
  webhookPath: string;      // The inbound URL for this provider e.g. /webhooks/delivery/bolt
  feeNote: string;          // Informational fee structure description
}

// ── Credential field ──────────────────────────────────────────────────────────

function SecretField({
  label,
  value,
  onChange,
  placeholder,
  readOnly = false,
  hint,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  hint?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
        {label}
      </label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder}
          readOnly={readOnly}
          className={cn(
            'w-full rounded-2xl border border-[var(--border-default)] px-4 py-2.5 pr-10 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors',
            readOnly ? 'bg-[var(--bg-base)]' : 'bg-white',
          )}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          tabIndex={-1}
        >
          {visible ? (
            <EyeOff className="w-4 h-4" strokeWidth={1.5} />
          ) : (
            <Eye className="w-4 h-4" strokeWidth={1.5} />
          )}
        </button>
      </div>
      {hint && <p className="text-[12px] text-[var(--text-muted)] mt-1.5">{hint}</p>}
    </div>
  );
}

// ── Copy-to-clipboard field ───────────────────────────────────────────────────

function CopyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          readOnly
          className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 pr-10 text-[14px] text-[var(--text-primary)]"
        />
        <button
          type="button"
          onClick={handleCopy}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-[var(--state-success)]" strokeWidth={1.5} />
          ) : (
            <Copy className="w-4 h-4" strokeWidth={1.5} />
          )}
        </button>
      </div>
      {hint && <p className="text-[12px] text-[var(--text-muted)] mt-1.5">{hint}</p>}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ label, description, checked, onChange }: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)] last:border-0">
      <div className="flex-1">
        <p className="text-[14px] font-semibold text-[var(--text-primary)]">{label}</p>
        <p className="text-[13px] text-[var(--text-muted)] mt-0.5">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={cn(
          'relative w-12 h-6 rounded-full transition-all',
          checked ? 'bg-[var(--gradient-to)]' : 'bg-[var(--bg-elevated)]',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform',
            checked ? 'transform translate-x-6' : '',
          )}
        />
      </button>
    </div>
  );
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins   = Math.floor(diffMs / 60_000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Main component ────────────────────────────────────────────────────────────

interface ProviderDetailPageProps {
  meta: ProviderMeta;
}

export const ProviderDetailPage = ({ meta }: ProviderDetailPageProps) => {
  const qc = useQueryClient();

  const [apiKey, setApiKey]         = useState('');
  const [apiSecret, setApiSecret]   = useState('');
  const [sandboxMode, setSandboxMode] = useState(false);
  const [saved, setSaved]           = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    latencyMs?: number;
    error?: string;
  } | null>(null);

  const WEBHOOK_URL = `https://api.chekit.co.ke/api/v1${meta.webhookPath}`;

  const { data: existingConfig } = useQuery({
    queryKey: ['delivery', 'providers', 'platform-config', meta.slug],
    queryFn: () => deliveryService.getProviderConfig(meta.slug),
  });

  // Platform activation status (delivery_providers.is_active)
  const { data: allProviders } = useQuery({
    queryKey: ['delivery', 'providers'],
    queryFn: () => deliveryService.listProviders(),
  });
  const providerInfo = allProviders?.find((p) => p.slug === meta.slug);

  const activationMutation = useMutation({
    mutationFn: (active: boolean) => deliveryService.setProviderActivation(meta.slug, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['delivery', 'providers'] });
      void qc.invalidateQueries({ queryKey: ['delivery', 'providers', 'summary'] });
    },
  });

  const { data: merchantConfigs, isLoading: merchantConfigsLoading } = useQuery({
    queryKey: ['delivery', 'third-party-configs', meta.slug],
    queryFn: () => deliveryService.listThirdPartyConfigs({ provider: meta.slug, limit: 50 }),
  });

  const { data: webhookEvents, isLoading: webhookEventsLoading } = useQuery({
    queryKey: ['delivery', 'webhook-events', meta.slug],
    queryFn: () => deliveryService.listWebhookEvents(meta.slug, 10),
    refetchInterval: 30_000,
  });

  const { data: apiCallLogs, isLoading: apiCallLogsLoading } = useQuery({
    queryKey: ['delivery', 'api-call-logs', meta.slug],
    queryFn: () => deliveryService.listApiCallLogs(meta.slug, 20),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (existingConfig?.isConfigured && !apiKey) {
      setSandboxMode(existingConfig.sandboxMode);
    }
  }, [existingConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: () =>
      deliveryService.updateProviderConfig(meta.slug, {
        apiKey,
        apiSecret: apiSecret || undefined,
        sandboxMode,
      }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      void qc.invalidateQueries({ queryKey: ['delivery', 'providers', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['delivery', 'third-party-configs'] });
    },
  });

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await deliveryService.testProviderConnection(meta.slug);
      setTestResult(result);
    } catch {
      setTestResult({ success: false, error: 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <PageGuard permission="settings.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Back link */}
        <Link
          to="/delivery/providers"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Providers
        </Link>

        {/* Page header */}
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-[22px]',
              meta.colorClass,
            )}
          >
            {meta.initial}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                {meta.name}
              </h1>
              {existingConfig?.isConfigured && (
                <span className="rounded-full bg-[var(--state-success-bg)] border border-[var(--state-success-border)] text-[var(--state-success)] text-[11px] font-semibold px-3 py-1">
                  Configured ({existingConfig.sandboxMode ? 'Sandbox' : 'Live'})
                </span>
              )}
              {providerInfo && !providerInfo.isActive && (
                <span className="rounded-full bg-[var(--state-danger-bg)] border border-[var(--state-danger-border)] text-[var(--state-danger)] text-[11px] font-semibold px-3 py-1">
                  Deactivated platform-wide
                </span>
              )}
            </div>
            <p className="text-[14px] text-[var(--text-muted)]">{meta.tagline}</p>
          </div>
        </div>

        {/* Platform Activation */}
        <div className="rounded-3xl bg-white shadow-soft p-8">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Platform Activation</h2>
              <p className="text-[14px] text-[var(--text-muted)] mt-0.5 max-w-[60ch]">
                When deactivated, merchants no longer see {meta.name} in their portal, cannot save
                configurations that use it, and no dispatch requests are sent to it. Existing
                in-flight deliveries still receive webhook updates.
              </p>
            </div>
            <Toggle
              label={providerInfo?.isActive ? 'Active' : 'Deactivated'}
              description={providerInfo?.isActive ? 'Visible to merchants, dispatch allowed' : 'Hidden from merchants, dispatch blocked'}
              checked={providerInfo?.isActive ?? true}
              onChange={() => {
                if (providerInfo && !activationMutation.isPending) {
                  activationMutation.mutate(!providerInfo.isActive);
                }
              }}
            />
          </div>
          {activationMutation.isError && (
            <p className="mt-3 text-[13px] text-[var(--state-danger)]">Failed to update activation. Try again.</p>
          )}
        </div>

        {/* Integration Settings */}
        <div className="rounded-3xl bg-white shadow-soft p-8 space-y-6">
          <div className="border-b border-[var(--border-subtle)] pb-5">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Integration Settings</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              API credentials for the {meta.name} delivery API
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <SecretField
              label="API Key"
              value={apiKey}
              onChange={setApiKey}
              placeholder={
                existingConfig?.isConfigured && !apiKey
                  ? `Currently set: ${existingConfig.apiKeyMasked ?? ''}`
                  : 'Enter API key...'
              }
              hint="Provided by your provider developer console"
            />
            <SecretField
              label="API Secret"
              value={apiSecret}
              onChange={setApiSecret}
              placeholder="Enter API secret..."
              hint="Leave blank if provider uses key-only auth"
            />
            <CopyField
              label="Webhook URL (our inbound)"
              value={WEBHOOK_URL}
              hint="Register this URL in your provider developer console to receive events"
            />
          </div>

          <Toggle
            label="Sandbox Mode"
            description="Use test API keys only; no real dispatches will be sent"
            checked={sandboxMode}
            onChange={() => setSandboxMode((v) => !v)}
          />

          {/* Test connection */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing || !apiKey}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Zap className="w-4 h-4" strokeWidth={1.5} />
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            {testResult && (
              <div
                className={cn(
                  'mt-3 rounded-2xl p-3 text-[13px] inline-flex items-center gap-2',
                  testResult.success
                    ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                    : 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
                )}
              >
                {testResult.success ? (
                  <>
                    <Check className="w-4 h-4" strokeWidth={1.5} />
                    Connection successful
                    {testResult.latencyMs != null && ` (${testResult.latencyMs}ms)`}
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                    {testResult.error || 'Connection failed'}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3">
            <button
              onClick={() => {
                setApiKey('');
                setApiSecret('');
                setSandboxMode(false);
              }}
              className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
            >
              Discard
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !apiKey}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              {saved ? (
                <>
                  <Check className="w-4 h-4" strokeWidth={1.5} />
                  Saved
                </>
              ) : saveMutation.isPending ? (
                'Saving...'
              ) : (
                'Save Credentials'
              )}
            </button>
          </div>
        </div>

        {/* Fee Structure */}
        <div className="rounded-3xl bg-white shadow-soft p-8">
          <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Fee Structure</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Informational only: typical rates for this provider
            </p>
          </div>
          <div className="rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-default)] p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[var(--text-accent)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-[13px] text-[var(--text-secondary)]">{meta.feeNote}</p>
          </div>
        </div>

        {/* Availability */}
        <div className="rounded-3xl bg-white shadow-soft p-8">
          <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Merchant Availability</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Merchants that have configured this provider
            </p>
          </div>
          {merchantConfigsLoading ? (
            <div className="py-10 text-center text-[14px] text-[var(--text-muted)]">Loading...</div>
          ) : (merchantConfigs?.data.length ?? 0) === 0 ? (
            <div className="text-center py-10 text-[14px] text-[var(--text-muted)]">
              <Webhook className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-3" strokeWidth={1.5} />
              <p>No merchant configurations yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Business</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Branch</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Mode</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Webhook</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {merchantConfigs?.data.map((cfg) => (
                  <tr key={cfg.id} className="hover:bg-[var(--bg-base)] transition-colors">
                    <td className="px-4 py-3 text-[14px] text-[var(--text-primary)]">{cfg.businessName || '—'}</td>
                    <td className="px-4 py-3 text-[13px] text-[var(--text-muted)]">{cfg.branchName || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                          cfg.apiMode === 'live'
                            ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                            : 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
                        )}
                      >
                        {cfg.apiMode}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          cfg.isActive
                            ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                            : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                        )}
                      >
                        {cfg.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[var(--text-muted)]">
                      {cfg.webhookConfigured ? 'Configured' : 'Not set'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* API Call History */}
        <div className="rounded-3xl bg-white shadow-soft p-8">
          <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">API Call History</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Outbound calls Chekit made to {meta.name}: dispatch, cancel, track, test
            </p>
          </div>

          {apiCallLogsLoading ? (
            <div className="py-10 text-center text-[14px] text-[var(--text-muted)]">Loading call history...</div>
          ) : !apiCallLogs || apiCallLogs.length === 0 ? (
            <div className="text-center py-10">
              <Zap className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[14px] text-[var(--text-muted)]">No outbound API calls yet.</p>
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Test the connection or dispatch an order to see calls here.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Call type</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Result</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Duration</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {apiCallLogs.map((log: ApiCallLog) => (
                  <tr key={log.id} className="hover:bg-[var(--bg-base)] transition-colors">
                    <td className="px-4 py-3">
                      <code className="text-[13px] text-[var(--text-primary)] capitalize">{log.callType}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          log.success
                            ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                            : 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
                        )}
                      >
                        {log.httpStatus ? `${log.httpStatus}` : log.success ? 'OK' : 'Error'}
                      </span>
                      {log.errorMessage && (
                        <div className="text-[11px] text-[var(--state-danger)] mt-0.5 max-w-[200px] truncate" title={log.errorMessage}>
                          {log.errorMessage}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[var(--text-muted)]">
                      {log.durationMs != null ? `${log.durationMs} ms` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)]">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {relativeTime(log.createdAt)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Webhook Events */}
        <div className="rounded-3xl bg-white shadow-soft p-8">
          <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Recent Webhook Events</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Last 10 events for {meta.name}, refreshed every 30 seconds
            </p>
          </div>

          {webhookEventsLoading ? (
            <div className="py-10 text-center text-[14px] text-[var(--text-muted)]">Loading events...</div>
          ) : !webhookEvents || webhookEvents.length === 0 ? (
            <div className="text-center py-10">
              <Webhook className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[14px] text-[var(--text-muted)]">No webhook events yet.</p>
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Events appear here once {meta.name} sends callbacks to the registered webhook URL.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Direction
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Event
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Received
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {webhookEvents.map((ev: WebhookEvent) => (
                  <tr key={ev.id} className="hover:bg-[var(--bg-base)] transition-colors">
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          ev.direction === 'inbound'
                            ? 'bg-[var(--bg-elevated)] text-[var(--text-accent)]'
                            : 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
                        )}
                      >
                        {ev.direction}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-[13px] text-[var(--text-primary)]">{ev.eventType}</code>
                      {ev.externalId && (
                        <div className="text-[11px] text-[var(--text-muted)] mt-0.5 font-mono">{ev.externalId}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)]">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {relativeTime(ev.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          ev.status === 'processed'
                            ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                            : ev.status === 'failed'
                            ? 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]'
                            : 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
                        )}
                      >
                        {ev.status === 'processed' ? '200 OK' : ev.status === 'failed' ? 'Error' : 'Pending'}
                      </span>
                      {ev.errorMessage && (
                        <div className="text-[11px] text-[var(--state-danger)] mt-0.5">{ev.errorMessage}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageGuard>
  );
};
